'use client'

import type { AppErrors } from '@/components/page-specific/catering/common/types/AppErrors'
import { basketLocationToAddress } from '@caterdesk/utils--gm-validation'

import { format, add, parse } from 'date-fns'
import _ from 'lodash'
import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'
import { useShallow } from 'zustand/react/shallow'

import type { UserBasketSettings } from '../domain/user-basket-settings'

interface AppState {
  // State
  userState: UserBasketSettings
  errors: AppErrors

  // Actions
  updateUserState: (userState: Partial<UserBasketSettings>) => void
  setErrors: (errors: AppErrors) => void
  clearErrors: () => void

  // Computed helpers
  isInitialized: () => boolean
  hasValidLocation: () => boolean
  hasValidDateTime: () => boolean
  formatTimeRange: (timeString?: string) => string | null
}

const defaultUserState: UserBasketSettings = {}

export const useAppStateStore = create<AppState>()(
  devtools(
    persist(
      immer((set, get) => ({
        // Initial state
        userState: defaultUserState,
        errors: {},

        // Actions
        updateUserState: (newUserState: Partial<UserBasketSettings>) =>
          set((state) => {
            state.userState = { ...defaultUserState, ...state.userState, ...newUserState }
          }),

        setErrors: (newErrors: AppErrors) =>
          set((state) => {
            const filteredErrors = _.omitBy(newErrors, _.isNull)
            state.errors = { ...filteredErrors }
          }),

        clearErrors: () =>
          set((state) => {
            state.errors = {}
          }),

        // Computed helpers
        isInitialized: () => {
          const { userState } = get()
          return !!userState.initalised
        },

        hasValidLocation: () => {
          const { userState } = get()
          if (!userState.location) return false

          const locationObject = basketLocationToAddress(userState.location)
          return !!(locationObject?.postcode && locationObject?.street)
        },

        hasValidDateTime: () => {
          const { userState } = get()
          return !!(userState.date && userState.time)
        },

        formatTimeRange: (timeString?: string) => {
          if (!timeString) return null

          // Parse time range like "14:30 - 15:00"
          const [startTime, endTime] = timeString.split(' - ')
          if (!startTime || !endTime) return timeString

          try {
            const start = parse(startTime, 'HH:mm', new Date())
            const end = parse(endTime, 'HH:mm', new Date())
            return `${format(start, 'HH:mm')} - ${format(end, 'HH:mm')}`
          } catch {
            return timeString
          }
        },
      })),
      {
        name: 'app-state-storage',
        partialize: (state: AppState) => ({
          userState: state.userState,
        }),
      },
    ),
    {
      name: 'app-state-store',
    },
  ),
)

// Utility functions for date manipulation using date-fns
export const appStateUtils = {
  /**
   * Creates a date-time from date timestamp and time string
   */
  createDateTime: (dateTimestamp: number, timeString: string): Date | null => {
    try {
      const baseDate = new Date(dateTimestamp)
      const [hours, minutes] = timeString.split(':')

      if (!hours || !minutes) return null

      const dateTime = new Date(baseDate)
      dateTime.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0)

      return dateTime
    } catch {
      return null
    }
  },

  /**
   * Creates a time range string (e.g., "14:30 - 15:00")
   */
  createTimeRange: (startDateTime: Date, durationMinutes = 30): string => {
    const endDateTime = add(startDateTime, { minutes: durationMinutes })
    return `${format(startDateTime, 'HH:mm')} - ${format(endDateTime, 'HH:mm')}`
  },

  /**
   * Parses query parameters into user state updates
   */
  parseQueryToUserState: (query: {
    postcode?: string
    street?: string
    date?: string
    time?: string
  }): Partial<UserBasketSettings> => {
    const updates: Partial<UserBasketSettings> = {}

    // Handle date
    if (query.date) {
      try {
        const parsedDate = new Date(query.date)
        if (!Number.isNaN(parsedDate.getTime())) {
          let finalDateTime = parsedDate

          // Handle time if provided
          if (query.time) {
            const [hours, minutes] = query.time.split(':')
            if (hours && minutes) {
              finalDateTime = new Date(parsedDate)
              finalDateTime.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0)

              // Create time range
              updates.time = appStateUtils.createTimeRange(finalDateTime)
            }
          }

          updates.date = finalDateTime.getTime()
        }
      } catch {
        // Invalid date, ignore
      }
    }

    return updates
  },

  /**
   * Gets the date object components from user state
   */
  getDateTimeComponents: (userState: UserBasketSettings) => {
    if (!userState.date) return null

    const date = new Date(userState.date)
    let hour = 0
    let minute = 0

    if (userState.time) {
      const timeMatch = userState.time.match(/^(\d{1,2}):(\d{2})/)
      if (timeMatch) {
        hour = parseInt(timeMatch[1], 10)
        minute = parseInt(timeMatch[2], 10)
      }
    }

    return {
      date: date.getDate(),
      month: date.getMonth(),
      year: date.getFullYear(),
      hour,
      minute,
    }
  },
}

export const useUserLocation = () => useAppStateStore((state) => state.userState.location)
export const useUserDate = () => useAppStateStore((state) => state.userState.date)
export const useUserTime = () => useAppStateStore((state) => state.userState.time)
export const useUserHeadCount = () => useAppStateStore((state) => state.userState.headCount)
export const useUserVendor = () => useAppStateStore((state) => state.userState.vendor)
export const useUserInitialized = () => useAppStateStore((state) => state.userState.initalised)
export const useUserStateSelector = <T>(selector: (userState: UserBasketSettings) => T) =>
  selector(useAppStateStore((state) => state.userState))
export const useAppStateErrors = () =>
  useAppStateStore(
    useShallow((state) => ({
      errors: state.errors,
      setErrors: state.setErrors,
      clearErrors: state.clearErrors,
    })),
  )
export const useUserStateActions = () =>
  useAppStateStore(
    useShallow((state) => ({
      updateUserState: state.updateUserState,
      clearErrors: state.clearErrors,
    })),
  )
export const useAppErrors = () => useAppStateStore((state) => state.errors)
